import { MessageFormatElement } from "react-intl";
import { ApiParams } from "./Api";
import { Notification } from "./Notification";
import { NavigateFunction } from "react-router-dom";
import { Dispatch } from "@reduxjs/toolkit";
import { AppContext, AuthRoles } from "@lib/types";

export type NestedKeys<T> = T extends object
  ? {
      [K in keyof T]: `${Exclude<K, symbol>}${NestedKeys<T[K]> extends never ? "" : `.${NestedKeys<T[K]>}`}`;
    }[keyof T]
  : never;

export enum ErrorMessages {
  TwoFactorAuthenticationRequired = "TwoFactorAuthenticationRequired",
  WrongTwoFactorAuthenticationCode = "WrongTwoFactorAuthenticationCode",
  UnexpectedError = "UnexpectedError",
  WrongUsernameOrPassword = "WrongUsernameOrPassword",
  LoginFromAnotherIP = "LoginFromAnotherIP",
  RevokedToken = "RevokedToken",
  InvalidInput = "InvalidInput",
  NotResolved = "NotResolved",
  AdditionalAuthorizationRequired = "AdditionalAuthorizationRequired",
}

type ConvertNumbers<T> = {
  [K in keyof T]: T[K] extends number ? number : T[K];
};

export function convertNumericFields<T extends Record<string, any>>(
  data: T,
): ConvertNumbers<T> {
  const result: Partial<ConvertNumbers<T>> = {};

  for (const key in data) {
    if (typeof data[key] === "string" && !isNaN(Number(data[key]))) {
      result[key] = Number(data[key]) as ConvertNumbers<T>[typeof key];
    } else {
      result[key] = data[key];
    }
  }

  return result as ConvertNumbers<T>;
}

export function convertNumericFieldsInArray<T extends Record<string, any>>(
  dataArray: T[] = [],
): ConvertNumbers<T>[] {
  return dataArray.map((item) => convertNumericFields(item));
}

export type LocationState = {
  notificationState?: Notification;
};

export type UseQueriesProps<T = undefined> = {
  messages: Record<string, string> | Record<string, MessageFormatElement[]>;
  id?: string;
  enabled?: boolean;
  params?: ApiParams<T>;
  queryKey?: any[];
  navigate?: NavigateFunction;
  dispatch?: Dispatch<any>;
  onSuccess?: () => void;
  onError?: () => void;
};

export type IconProps = {
  width?: number;
  height?: number;
  color?: string;
};

export type TableColumnConfig<T extends Record<string, any>> = {
  noFilter?: boolean;
  noSort?: boolean;
  noPagination?: boolean;
  noSearch?: boolean;
  noAction?: boolean;
  noExpand?: boolean;
  noInfo?: boolean;
  hiddenColumns?: T[];
};

export type TableColumnsProps<T extends Record<string, any>> = {
  userRole?: AuthRoles;
  apiData: T[];
  messages: Record<string, string> | Record<string, MessageFormatElement[]>;
  params?: ApiParams;
  context?: AppContext;
  detailContext?: AppContext;
  navigate?: NavigateFunction;
  columnsConfig?: TableColumnConfig<T>;
};

export type AvatarSize =
  | "xx-small"
  | "x-small"
  | "small"
  | "medium"
  | "large"
  | "x-large"
  | "xx-large";
